import { ApiSet, BaseResponse, IndexApiSet, useDeleteApi, useDownloadApi, useIndexApi, usePostApi, usePutApi } from 'utils/network/api_hooks'
import { Form, useEffectSkipFirst } from 'utils/hooks'
import { CraifHttpClient } from '../utils/network/axios'
import { PagingResponse } from 'entities'
import { MaintenanceLedgerItem, MaintenanceLedgerItemForm, MaintenanceLedgerItemSearchForm } from 'entities/maintenance_ledger_item'

type MaintenanceLedgerItemsResponse = PagingResponse & {
    results: MaintenanceLedgerItem[]
}

export function useFetchMaintenanceLedgerItemsApi(
    searchForm?: Form<MaintenanceLedgerItemSearchForm>
): IndexApiSet<MaintenanceLedgerItemsResponse> & { execute: () => void } {
    const apiPath = 'maintenance_ledger_items/'
    const api = useIndexApi<MaintenanceLedgerItemsResponse>(new CraifHttpClient(), {
        initialResponse: { count: 0, results: [] },
        initialState: { page: searchForm?.object?.page || 1, perPage: searchForm?.object?.perPage || 50 },
    })

    const execute = (): void => {
        api.execute(apiPath, { params: searchForm?.object })
    }

    useEffectSkipFirst(() => {
        searchForm?.update((f) => {
            if (api.pageSet.page) f.page = api.pageSet.page
            if (api.pageSet.perPage) f.perPage = api.pageSet.perPage
        })
    }, [api.pageSet.page, api.pageSet.perPage])

    useEffectSkipFirst(() => {
        execute()
    }, [searchForm?.object])

    return { ...api, execute: execute }
}

export function useDownloadCsvMaintenanceLedgerItemsApi(searchForm: Form<MaintenanceLedgerItemSearchForm>): ApiSet<BaseResponse> & { execute: () => void } {
    const apiPath = 'maintenance_ledger_items/csv/'
    const api = useDownloadApi<BaseResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (): void => {
        api.execute(apiPath, 'maintenance_ledger_item', searchForm.object)
    }

    return { ...api, execute: execute }
}

export function usePostMaintenanceLedgerItemApi(): ApiSet<BaseResponse> & { execute: (form: Form<MaintenanceLedgerItemForm>) => void } {
    const apiSet = usePostApi<BaseResponse, MaintenanceLedgerItemForm>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (form: Form<MaintenanceLedgerItemForm>) => {
        const apiPath = `maintenance_ledger_items/`
        apiSet.execute(apiPath, form)
    }

    return { ...apiSet, execute: execute }
}

export function usePutMaintenanceLedgerItemApi(): ApiSet<BaseResponse> & { execute: (form: Form<MaintenanceLedgerItemForm>) => void } {
    const apiSet = usePutApi<BaseResponse, MaintenanceLedgerItemForm>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (form: Form<MaintenanceLedgerItemForm>) => {
        const apiPath = `maintenance_ledger_items/${form.object.id}/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function useBulkPutMaintenanceLedgerItemsApi(): ApiSet<BaseResponse> & { execute: (form: Form<MaintenanceLedgerItemForm[]>) => void } {
    const apiSet = usePutApi<BaseResponse, MaintenanceLedgerItemForm[]>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<MaintenanceLedgerItemForm[]>) => {
        const apiPath = `maintenance_ledger_items/bulk_update/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function useDeleteMaintenanceLedgerItemApi(): ApiSet<BaseResponse> & {
    execute: (id: number) => void
} {
    const api = useDeleteApi<BaseResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (id: number): void => {
        const apiPath = `maintenance_ledger_items/${id}/`
        api.execute(apiPath)
    }

    return { ...api, execute: execute }
}
